/**
 * Shared layout for Settings sections — header, card body, conditional sticky save bar.
 */
import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import { useSettingsForm } from '../SettingsFormContext';

const SETTINGS_CONTENT_MAX = 960;

const SettingsPageLayout = ({
  title,
  description,
  children,
  maxWidth = SETTINGS_CONTENT_MAX,
  onSave,
  onCancel,
  saving = false,
  noCard = false,
}) => {
  const { isDirty } = useSettingsForm();
  const showBar = Boolean(onSave) && isDirty;

  const body = noCard ? (
    <Stack spacing={3} sx={{ width: '100%' }}>
      {children}
    </Stack>
  ) : (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={3}>{children}</Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        pb: showBar ? 2 : 0,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} component="h2">
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
            {description}
          </Typography>
        ) : null}
      </Box>

      {body}

      {showBar ? (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mt: 3,
            py: 2,
            px: 2,
            mx: noCard ? 0 : -0.5,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            boxShadow: (theme) => theme.shadows[4],
          }}
        >
          <Alert severity="warning" sx={{ flex: '1 1 200px', py: 0.5 }}>
            Unsaved changes — save before leaving this page.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {onCancel ? (
              <Button variant="outlined" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            ) : null}
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default SettingsPageLayout;
